import { useCallback, useEffect, useRef, useState } from 'react';

/** Mensaje legible de un error del backend: el código de negocio si lo hay, o un texto genérico. */
export const reportErrorMessage = (err) => {
  const code = err?.response?.data?.error_message || err?.response?.data?.message;
  if (code === 'invalid_days') return 'El rango de días no es válido.';
  if (code === 'invalid_granularity') return 'La agrupación no es válida.';
  if (err?.response?.status === 403) return 'Tu cuenta no tiene acceso a los reportes.';
  return 'No se pudo cargar el reporte. Probá de nuevo en unos minutos.';
};

/**
 * Carga un reporte y lo vuelve a pedir cuando cambian los parámetros. Mientras recarga conserva los datos anteriores
 * (`refreshing`), así la pantalla no salta ni parpadea; `loading` es solo la primera carga.
 */
export default function useReport(fetcher, params) {
  const [state, setState] = useState({ data: null, error: '', loading: true, refreshing: false });
  const request = useRef(0);
  const key = JSON.stringify(params);
  // El fetcher se guarda en una ref: recargar depende SOLO de los parámetros, nunca de la identidad de la función
  // (una función nueva en cada render provocaría un bucle de pedidos).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    const id = ++request.current;
    setState((s) => ({ ...s, error: '', loading: s.data === null, refreshing: s.data !== null }));
    try {
      const data = await fetcherRef.current(JSON.parse(key));
      if (id === request.current) setState({ data, error: '', loading: false, refreshing: false });
    } catch (err) {
      if (id === request.current) setState((s) => ({ ...s, error: reportErrorMessage(err), loading: false, refreshing: false }));
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}
